import { Controller } from "@hotwired/stimulus"

import { CONSENT } from "@/helpers/consent"
import { sessionStorage } from "@/helpers/storage"
import SearchLocationController from "./search_location_controller"

// Carries the region a user searched for across a browsing session so the mobile search bar can
// prefill it as they browse venues. We use `sessionStorage` (per-tab, cleared on close) rather than
// a param/cookie because venue pages are page-cached and can't vary the baked-in region.
const STORAGE_KEY = "search-selected-region"

interface SearchRegion {
  name: string
  slug: string
}

export default class extends Controller {
  static outlets = ["search-location"]
  declare readonly searchLocationOutlet: SearchLocationController

  static targets = ["summary"]
  declare readonly summaryTarget: HTMLSpanElement

  connect(): void {
    // Venue pages restore the searched region; every other page (search, home, ...) persists the
    // current search-bar location, so detouring via a region-less page (e.g. home) clears it.
    if (this.isVenuePage) {
      this.restoreSessionRegion()
    } else {
      this.persistSelectedRegion()
    }
  }

  private get isVenuePage(): boolean {
    return window.location.pathname.startsWith("/venues/")
  }

  // Remember the region currently selected in the search bar, or forget any stored region when the
  // selection isn't a region (a town, venue, "spas near me", or nothing).
  private persistSelectedRegion(): void {
    const { locationType, name, slug } = this.searchLocationOutlet.selectedLocationValue

    if (locationType === "region" && name && slug) {
      sessionStorage?.setItemIfConsent(
        CONSENT.FUNCTIONALITY,
        STORAGE_KEY,
        JSON.stringify({ name, slug })
      )
    } else {
      sessionStorage?.removeItem(STORAGE_KEY)
    }
  }

  private readSearchRegion(): Nullable<SearchRegion> {
    try {
      const raw = sessionStorage?.getItem(STORAGE_KEY)
      const { name, slug } = JSON.parse(raw) as Partial<SearchRegion>

      return typeof name === "string" && typeof slug === "string" ? { name, slug } : null
    } catch {
      return null
    }
  }

  // Override the baked-in venue region with the region the user searched for this session.
  private restoreSessionRegion(): void {
    const region = this.readSearchRegion()

    if (!region) {
      return
    }

    this.searchLocationOutlet.selectedLocationValue = {
      locationType: "region",
      maxDistance: "",
      name: region.name,
      slug: region.slug
    }

    this.searchLocationOutlet.setInputValue(region.name)
    this.summaryTarget.textContent = region.name
  }
}
